import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { EvaluationService } from '@services/core/evaluation.service';
import { EntityService } from '@services/core/entity.service';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { moduleGroups } from '@shared/module.utils';
import { applyGoalTargets, asArray, buildGoalMap, buildGoalPayload, buildHistoryParams, buildYearList, canLoadGoals, entityIdOf, firstEntity, goalErrorText, hasTargetValue, indexValue, isPendingDone, moduleIdOf, pendingState, pushGoalRow, pushSubRows, readTarget, showGoalError, sortCategories, sortGoalRows, takePending, toastGoalSaved, warnSelectEntity, warnTargetRequired } from './admin-goals-helpers';

@Component({
  selector: 'app-admin-goals',
  templateUrl: './admin-goals.component.html',
  styleUrls: ["./admin-goals.component.scss"],
  imports: [PanelComponent, FormsModule, NgbTooltipModule, NgSelectComponent, DatePipe],
  providers: [EvaluationService],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class AdminGoalsComponent implements OnInit {
  protected module: any
  protected groups: any[] = []
  protected selectedGroup: any
  protected entities: any[] = []
  protected selectedEntity: any = null
  protected yearSelected = new Date().getFullYear()
  protected years: number[] = []
  protected personalQuestions: any[] = []
  protected goalsByQuestion = signal<Record<string, any>>({})
  protected historyRows: any[] = []
  protected historyQuestion: any = null
  protected editingQuestionId = signal<string | null>(null)

  private evaluationService = inject(EvaluationService)
  private entityService = inject(EntityService)
  private route = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  private modalService = inject(NgbModal)

  ngOnInit() {
    const data = this.route.snapshot.data['goalsData'];
    this.module = data.module;
    this.setYears();
    this.groups = moduleGroups(this.module);
    this.selectedGroup = this.groups[0];
    this.loadModuleData();
  }

  private setYears() {
    this.years = buildYearList(new Date().getFullYear(), 2024);
  }

  private loadModuleData() {
    if (canLoadGoals(this.module, this.selectedGroup) === false) return;
    this.selectedEntity = null;
    this.entityService.getEntitiesGroup(this.selectedGroup)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((entities: any) => this.onEntities(entities));
  }

  /** Guarda entidades y carga categorías. */
  private onEntities(entities: any) {
    this.entities = asArray(entities);
    this.selectedEntity = firstEntity(this.entities);
    this.loadCategories();
  }

  private loadCategories() {
    this.evaluationService.getCategories(this.module.id, this.yearSelected, this.selectedGroup)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((categories: any) => this.onCategories(categories));
  }

  /** Inicia carga de preguntas personales. */
  private onCategories(categories: any) {
    this.loadPersonalQuestions(asArray(categories));
  }

  private loadPersonalQuestions(categories: any[]) {
    if (categories.length === 0) return this.finishEmpty();
    this.fetchSortedQuestions(sortCategories(categories));
  }

  /** Finaliza cuando no hay categorías. */
  private finishEmpty() {
    this.personalQuestions = [];
    this.loadGoals();
  }

  /** Pide preguntas de cada categoría. */
  private fetchSortedQuestions(sorted: any[]) {
    const all: any[] = [];
    const state = pendingState(sorted.length);
    for (const cat of sorted) this.fetchCategoryQuestions(all, cat, state);
  }

  /** Pide preguntas de una categoría. */
  private fetchCategoryQuestions(all: any[], cat: any, state: { left: number }) {
    this.evaluationService.getQuestions(cat.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((questions: any) => this.onCategoryQuestions(all, cat, questions, state));
  }

  /** Acumula preguntas y cierra al completar. */
  private onCategoryQuestions(all: any[], cat: any, questions: any, state: { left: number }) {
    this.collectPersonal(all, cat, asArray(questions));
    takePending(state);
    if (isPendingDone(state) === true) this.finishQuestions(all);
  }

  /** Ordena y publica preguntas. */
  private finishQuestions(all: any[]) {
    sortGoalRows(all);
    this.personalQuestions = all;
    this.loadGoals();
  }

  private collectPersonal(all: any[], cat: any, questions: any[]) {
    const catNum = indexValue(cat) + 1;
    for (const q of asArray(questions)) this.appendQuestion(all, cat, catNum, q);
  }

  /** Agrega fila de pregunta y sus subfilas. */
  private appendQuestion(all: any[], cat: any, catNum: number, q: any) {
    pushGoalRow(all, cat, catNum, q);
    pushSubRows(all, cat, catNum, q);
  }

  onGroupChange() {
    this.loadModuleData();
  }

  onYearChange() {
    this.loadModuleData();
  }

  onEntityChange() {
    this.loadGoals();
  }

  private loadGoals() {
    if (this.module) this.fetchGoals();
  }

  /** Pide metas personales. */
  private fetchGoals() {
    this.evaluationService.getPersonalGoals(this.module.id, entityIdOf(this.selectedEntity), this.yearSelected)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((goals: any) => this.onGoals(goals));
  }

  /** Publica metas recibidas. */
  private onGoals(goals: any) {
    const map = buildGoalMap(asArray(goals));
    this.goalsByQuestion.set(map);
    applyGoalTargets(this.personalQuestions, map);
  }

  goalFor(questionId: string): any {
    return this.goalsByQuestion()[questionId];
  }

  startEdit(question: any) {
    this.editingQuestionId.set(question.id);
  }

  cancelEdit(question: any) {
    question.targetValue = readTarget(this.goalsByQuestion(), question.id);
    this.editingQuestionId.set(null);
  }

  saveGoal(question: any) {
    if (entityIdOf(this.selectedEntity) === null) return warnSelectEntity();
    this.persistGoal(question, entityIdOf(this.selectedEntity) as string);
  }

  /** Persiste meta de pregunta. */
  private persistGoal(question: any, entityId: string) {
    const target = Number(question.targetValue);
    if (hasTargetValue(target) === false) return warnTargetRequired();
    this.sendGoal(question, entityId, target);
  }

  /** Envía meta a API. */
  private sendGoal(question: any, entityId: string, target: number) {
    const payload = buildGoalPayload(this.goalFor(question.id), question.id, entityId, this.module.id, this.yearSelected, target);
    this.evaluationService.upsertPersonalGoal(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.onGoalSaved(),
      error: (err: any) => this.onGoalError(err, 'No se pudo guardar'),
    });
  }

  /** Confirma guardado de meta. */
  private onGoalSaved() {
    toastGoalSaved();
    this.editingQuestionId.set(null);
    this.loadGoals();
  }

  /** Muestra error de meta. */
  private onGoalError(err: any, fallback: string) {
    showGoalError(goalErrorText(err, fallback));
  }

  removeGoal(question: any) {
    if (this.goalFor(question.id)) this.deleteGoal(question);
  }

  /** Borra meta existente. */
  private deleteGoal(question: any) {
    const existing = this.goalFor(question.id);
    this.evaluationService.deletePersonalGoal(existing.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadGoals(),
      error: (err: any) => this.onGoalError(err, 'No se pudo eliminar'),
    });
  }

  openHistory(modal: any, question: any) {
    this.historyQuestion = question;
    this.historyRows = [];
    this.fetchHistory(modal, question);
  }

  /** Pide historial de meta. */
  private fetchHistory(modal: any, question: any) {
    const params = buildHistoryParams(question.id, question.isPersonal, entityIdOf(this.selectedEntity), moduleIdOf(this.module), this.yearSelected);
    this.evaluationService.getPersonalGoalHistory(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (rows: any) => this.onHistory(modal, rows),
      error: (err: any) => this.onGoalError(err, 'No se pudo cargar el historial'),
    });
  }

  /** Publica historial y abre modal. */
  private onHistory(modal: any, rows: any) {
    this.historyRows = asArray(rows);
    this.modalService.open(modal, { size: 'lg', scrollable: true });
  }
}
